import { medalHistoryNoc } from "@seagames/contracts";
import { type MedalCount, type MedalHistoryData, type SportHistory, sportHistory } from "./medal-history";

/**
 * The admin "Strength map" (entries overview): every NOC × every sport of the
 * games, the NOC's past SEA Games tier in the sport overlaid with what it
 * declared now. Pure and data-free like `medal-history.ts`: the caller passes
 * the dataset (dynamic import, admin only), the games' sports
 * (`/sports/options`) and the EOI matrix rows (`/entries/matrix?phase=EOI`,
 * whose cells already carry the EBN numbers). ≤ 11 × ~43 cells: computed in
 * the browser on purpose, nothing here is a list the server pages.
 *
 * History, never a prediction or a rule: a "gap" is a prompt for a
 * conversation with the NOC, not an error.
 */

/** Stored organisation code → the dataset's NOC code (the portal's: stored SIN is SGP). */
export const historyNoc = medalHistoryNoc;

export type StrengthMode = "EOI" | "EBN";
export const STRENGTH_SHOWS = ["all", "contested", "gaps"] as const;
export const SPORT_SORTS = ["code", "golds"] as const;
export const NOC_SORTS = ["name", "golds"] as const;
export type StrengthShow = (typeof STRENGTH_SHOWS)[number];
export type SportSort = (typeof SPORT_SORTS)[number];
export type NocSort = (typeof NOC_SORTS)[number];

/** What the map needs from a matrix cell (`EntryMatrixCell`); an estimate is null when not given (0062). */
export interface StrengthDeclared {
	sport_id: string;
	est_athletes_men: number | null;
	est_athletes_women: number | null;
	est_officials: number | null;
	expected_gold: number | null;
	expected_silver: number | null;
	expected_bronze: number | null;
	ebn_events: number;
	ebn_entries: number;
}

export interface StrengthSportRef {
	sport_id: string;
	code: string;
	name: string;
}

export interface StrengthOrg {
	organization: { id: string; name: string; code: string | null };
	cells: readonly StrengthDeclared[];
}

export interface StrengthSport extends StrengthSportRef {
	/** Golds won in the sport over the three Games, every NOC of the dataset. */
	golds: number;
	/** Held at least once in the three Games, with a medal table. */
	contested: boolean;
	/** At least one NOC on the map is strong in it and has not declared it. */
	hasGap: boolean;
}

export interface StrengthCell {
	sport: StrengthSport;
	history: SportHistory;
	/** Won gold in the sport (tier TOP / GOLD). */
	strong: boolean;
	/** The EOI row, with the EBN numbers; null when the sport is not in the EOI. */
	declared: StrengthDeclared | null;
	/** EOI mode: in the EOI. EBN mode: at least one EBN entry. */
	picked: boolean;
	/** Strong and not picked. */
	gap: boolean;
}

export interface StrengthRow {
	organization: StrengthOrg["organization"];
	/** The dataset's code; null when the organisation is not a NOC of the dataset. */
	noc: string | null;
	/** Aligned with `StrengthModel.sports`. */
	cells: StrengthCell[];
	golds: number;
	strong: number;
	strongPicked: number;
	/** Most golds first. */
	gaps: StrengthCell[];
}

export interface StrengthModel {
	sports: StrengthSport[];
	rows: StrengthRow[];
}

const ZERO: MedalCount = { g: 0, s: 0, b: 0 };
const medals = (m: MedalCount) => m.g + m.s + m.b;

function noHistory(data: MedalHistoryData, code: string): SportHistory {
	// An organisation the dataset does not know: the sport's Games, no numbers.
	const perGames = [...(data.sports[code]?.games ?? [])]
		.sort((a, b) => a.year - b.year)
		.map((g) => ({ year: g.year, contested: g.contested, hasData: g.medals !== null, source_urls: g.source_urls, notes: g.notes, ...ZERO }));
	return { tier: "NO_DATA", totals: ZERO, perGames, medalledIn: 0, contestedIn: 0, rank: null };
}

export function buildStrength(data: MedalHistoryData, sports: readonly StrengthSportRef[], orgs: readonly StrengthOrg[], mode: StrengthMode): StrengthModel {
	const known = new Set(data.nocs);
	const base = sports.map((s) => {
		const games = data.sports[s.code]?.games ?? [];
		const counted = games.filter((g) => g.contested && g.medals !== null);
		const golds = counted.reduce((n, g) => n + Object.values(g.medals ?? {}).reduce((k, m) => k + m.g, 0), 0);
		return { ...s, golds, contested: counted.length > 0, hasGap: false };
	});
	const rows: StrengthRow[] = orgs.map((o) => {
		const code = historyNoc(o.organization.code);
		const noc = code && known.has(code) ? code : null;
		const byId = new Map(o.cells.map((c) => [c.sport_id, c]));
		const cells = base.map((sport): StrengthCell => {
			const history = noc ? sportHistory(data, sport.code, noc) : noHistory(data, sport.code);
			const strong = history.tier === "TOP" || history.tier === "GOLD";
			const declared = byId.get(sport.sport_id) ?? null;
			const picked = mode === "EOI" ? declared !== null : (declared?.ebn_entries ?? 0) > 0;
			return { sport, history, strong, declared, picked, gap: strong && !picked };
		});
		const strongCells = cells.filter((c) => c.strong);
		return {
			organization: o.organization,
			noc,
			cells,
			golds: cells.reduce((n, c) => n + c.history.totals.g, 0),
			strong: strongCells.length,
			strongPicked: strongCells.filter((c) => c.picked).length,
			gaps: cells.filter((c) => c.gap).sort((a, b) => b.history.totals.g - a.history.totals.g || medals(b.history.totals) - medals(a.history.totals) || a.sport.code.localeCompare(b.sport.code)),
		};
	});
	for (const [i, s] of base.entries()) s.hasGap = rows.some((r) => r.cells[i]?.gap);
	return { sports: base, rows };
}

/** The sport columns shown, as indexes into `model.sports`, in display order. */
export function visibleSports(model: StrengthModel, show: StrengthShow, sort: SportSort): number[] {
	const idx = model.sports.map((_, i) => i).filter((i) => {
		const s = model.sports[i];
		if (!s) return false;
		if (show === "contested") return s.contested;
		if (show === "gaps") return s.hasGap;
		return true;
	});
	const code = (i: number) => model.sports[i]?.code ?? "";
	const golds = (i: number) => model.sports[i]?.golds ?? 0;
	return idx.sort((a, b) => (sort === "golds" ? golds(b) - golds(a) : 0) || code(a).localeCompare(code(b)));
}

/** NOC rows in display order; names compare in the viewer's locale. */
export function sortRows(rows: readonly StrengthRow[], sort: NocSort, locale?: string): StrengthRow[] {
	const name = (r: StrengthRow) => r.organization.name;
	return [...rows].sort((a, b) => (sort === "golds" ? b.golds - a.golds : 0) || name(a).localeCompare(name(b), locale));
}

export interface TopNation {
	noc: string;
	rank: number;
	totals: MedalCount;
}

/** The best NOCs of the dataset in a sport over the three Games (gold, then medals), medal winners only. */
export function topNations(data: MedalHistoryData, code: string, n = 3): TopNation[] {
	return data.nocs
		.map((noc) => {
			const h = sportHistory(data, code, noc);
			return { noc, rank: h.rank ?? 0, totals: h.totals };
		})
		.filter((x) => medals(x.totals) > 0)
		.sort((a, b) => a.rank - b.rank || a.noc.localeCompare(b.noc))
		.slice(0, n);
}
